import { useEffect, useState, useCallback } from "react";
import { Flame, Plug, Server } from "lucide-react";
import { theme, card, inputStyle, btnPrimary, emptyFrame } from "../theme";
import { LoadingBlock } from "../components/Spinner";
import { api, apiErrorMessage } from "../api/client";

import { cbPalette } from "../lib/colourBlind";
type FirewallVendor = "fortinet" | "palo_alto" | "cisco";

interface SystemInfo {
  model:            string | null;
  firmwareVersion:  string | null;
  serialNumber:     string | null;
  hostname:         string | null;
  uptime:           string | null;
}

interface FirewallConnection {
  id:          string;
  vendor:      FirewallVendor;
  displayName: string;
  host:        string;
  port:        number | null;
  connected:   boolean;
  lastSync:    string | null;
  systemInfo:  SystemInfo | null;
  policyCount: number | null;
  createdAt:   string;
}

const VENDOR_COLORS: Record<FirewallVendor, string> = cbPalette({
  fortinet:  "#ef4444",
  palo_alto: "#f97316",
  cisco:     "#3b82f6",
});

function VendorDot({ vendor }: { vendor: FirewallVendor }) {
  return <Server size={16} color={VENDOR_COLORS[vendor]} />;
}

const VENDOR_META: Record<FirewallVendor, { label: string; icon: React.ReactNode; authFields: "apiKey" | "userpass" }> = {
  fortinet:  { label: "Fortinet FortiGate",         icon: <VendorDot vendor="fortinet" />,  authFields: "apiKey" },
  palo_alto: { label: "Palo Alto Networks",          icon: <VendorDot vendor="palo_alto" />, authFields: "apiKey" },
  cisco:     { label: "Cisco Firepower (FMC)",       icon: <VendorDot vendor="cisco" />,     authFields: "userpass" },
};

const VENDOR_HELP: Record<FirewallVendor, string> = {
  fortinet:  "In FortiGate, go to System → Administrators → Create New → REST API Admin. Set the trusted hosts to this server's IP and copy the generated API key.",
  palo_alto: "In PAN-OS, go to Device → Administrators, create a read-only admin, then generate an API key via the CLI: request api-key generate login <user> password <pass>.",
  cisco:     "Enter credentials for a read-only FMC admin account. The platform will generate a short-lived session token for each sync.",
};

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

function VendorBadge({ vendor }: { vendor: FirewallVendor }) {
  const m = VENDOR_META[vendor];
  return (
    <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted }}>
      {m.icon} {m.label}
    </span>
  );
}

interface FirewallFinding {
  id:        string;
  kind:      string;
  severity:  "high" | "medium" | "low";
  title:     string;
  detail:    string;
  positions: number[];
}

interface HygieneResponse {
  findings:   FirewallFinding[];
  summary:    { total: number; high: number; medium: number; low: number; anyAny: number };
  rules:      number;
  capturedAt: string | null;
}

interface PolicySnapshot {
  id:           string;
  snapshotType: string;
  summary:      string | null;
  capturedAt:   string;
}

const SEVERITY_STYLE: Record<FirewallFinding["severity"], { bg: string; fg: string }> = {
  high:   { bg: "var(--c-dangerBg)",  fg: "var(--c-dangerText)" },
  medium: { bg: "var(--c-warningBg)", fg: "var(--c-warningText)" },
  low:    { bg: "var(--c-faint)",     fg: "var(--c-muted)" },
};

const linkBtn: React.CSSProperties = {
  background: "none", border: "none", padding: 0, fontSize: 12, fontWeight: 600,
  color: "var(--c-accentText)", cursor: "pointer",
};

/** Rule hygiene (from the latest policy snapshot) and the snapshot history for one firewall. */
function FirewallDetails({ id }: { id: string }) {
  const [open, setOpen] = useState<"hygiene" | "history" | null>(null);
  const [hygiene, setHygiene] = useState<HygieneResponse | null>(null);
  const [snapshots, setSnapshots] = useState<PolicySnapshot[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function toggle(view: "hygiene" | "history") {
    if (open === view) { setOpen(null); return; }
    setOpen(view);
    setErr(null);
    try {
      if (view === "hygiene") setHygiene(await api.get<HygieneResponse>(`/estate/firewalls/${id}/findings`));
      else setSnapshots((await api.get<{ snapshots: PolicySnapshot[] }>(`/firewall/${id}/snapshots`)).snapshots);
    } catch (e) {
      setErr(apiErrorMessage(e, view === "hygiene" ? "Could not load rule hygiene." : "Could not load snapshot history."));
    }
  }

  return (
    <div style={{ marginTop: 12, borderTop: `1px solid ${theme.color.border}`, paddingTop: 10 }}>
      <div style={{ display: "flex", gap: 16 }}>
        <button type="button" style={linkBtn} onClick={() => toggle("hygiene")}>
          {open === "hygiene" ? "Hide rule hygiene" : "Rule hygiene"}
        </button>
        <button type="button" style={linkBtn} onClick={() => toggle("history")}>
          {open === "history" ? "Hide history" : "History"}
        </button>
      </div>
      {err && <div style={{ marginTop: 8, fontSize: 12, color: "var(--c-dangerText)" }}>{err}</div>}

      {open === "hygiene" && !err && (
        !hygiene ? <div style={{ marginTop: 8, fontSize: 12, color: theme.color.muted }}>Loading…</div> :
        !hygiene.capturedAt ? (
          <div style={{ marginTop: 8, fontSize: 12, color: theme.color.muted }}>No policy snapshot yet. Sync the firewall to analyse its rule base.</div>
        ) : (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 12, color: theme.color.muted, marginBottom: 8 }}>
              {hygiene.rules} rules analysed from the snapshot of {fmtDate(hygiene.capturedAt)}:{" "}
              <strong style={{ color: theme.color.text }}>{hygiene.summary.high} high, {hygiene.summary.medium} medium, {hygiene.summary.low} low</strong>
            </div>
            {hygiene.findings.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--c-successText)" }}>No rule hygiene findings.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {hygiene.findings.map((f) => (
                  <div key={f.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 12 }}>
                    <span style={{ background: SEVERITY_STYLE[f.severity].bg, color: SEVERITY_STYLE[f.severity].fg, fontSize: 11, fontWeight: 600, padding: "1px 8px", borderRadius: 3, textTransform: "capitalize", flexShrink: 0 }}>
                      {f.severity}
                    </span>
                    <div>
                      <div style={{ fontWeight: 600, color: theme.color.text }}>{f.title}</div>
                      <div style={{ color: theme.color.muted }}>{f.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      )}

      {open === "history" && !err && (
        !snapshots ? <div style={{ marginTop: 8, fontSize: 12, color: theme.color.muted }}>Loading…</div> :
        snapshots.length === 0 ? (
          <div style={{ marginTop: 8, fontSize: 12, color: theme.color.muted }}>No snapshots captured yet.</div>
        ) : (
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
            {snapshots.map((s) => (
              <div key={s.id} style={{ display: "flex", gap: 14, fontSize: 12 }}>
                <span style={{ color: theme.color.muted, minWidth: 110 }}>{fmtDate(s.capturedAt)}</span>
                <span style={{ color: theme.color.text }}>{s.summary ?? s.snapshotType}</span>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

interface AddFormState {
  vendor:      FirewallVendor;
  displayName: string;
  host:        string;
  port:        string;
  apiKey:      string;
  username:    string;
  password:    string;
}

const EMPTY_FORM: AddFormState = {
  vendor: "fortinet", displayName: "", host: "", port: "",
  apiKey: "", username: "", password: "",
};

export function ConnectFirewallPage() {
  const [connections, setConnections] = useState<FirewallConnection[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [syncing,  setSyncing]  = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error,    setError]    = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form,     setForm]     = useState<AddFormState>(EMPTY_FORM);

  const loadConnections = useCallback(async () => {
    try {
      const { connections: conns } = await api.get<{ connections: FirewallConnection[] }>("/firewall");
      setConnections(conns);
    } catch {
      setError("Failed to load firewall connections.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConnections(); }, [loadConnections]);

  function setField(key: keyof AddFormState, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        vendor:      form.vendor,
        displayName: form.displayName,
        host:        form.host,
        port:        form.port ? parseInt(form.port, 10) : null,
      };
      if (VENDOR_META[form.vendor].authFields === "apiKey") {
        body.apiKey = form.apiKey;
      } else {
        body.username = form.username;
        body.password = form.password;
      }
      await api.post("/firewall", body);
      setShowForm(false);
      setForm(EMPTY_FORM);
      await loadConnections();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Connection failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleSync(id: string) {
    setSyncing(id);
    setError(null);
    try {
      await api.post(`/firewall/${id}/sync`, {});
      await loadConnections();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Sync failed");
    } finally {
      setSyncing(null);
    }
  }

  async function handleRemove(id: string, name: string) {
    if (!confirm(`Remove firewall '${name}' and delete all policy snapshots?`)) return;
    setRemoving(id);
    try {
      await api.del(`/firewall/${id}`);
      await loadConnections();
    } catch {
      setError("Remove failed");
    } finally {
      setRemoving(null);
    }
  }

  if (loading) return <LoadingBlock label="Loading firewall connections…" />;

  const meta = VENDOR_META[form.vendor];

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <Flame size={22} color="var(--c-accentText)" />
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: theme.color.heading }}>
              Firewall Integrations
            </h2>
            {connections.length > 0 && (
              <span style={{ background: "var(--c-successBg)", color: "var(--c-successText)", fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 3 }}>
                {connections.length} connected
              </span>
            )}
          </div>
          <p style={{ margin: 0, fontSize: 13, color: theme.color.subtle }}>
            Pull live firewall policy and configuration status from Fortinet, Palo Alto, and Cisco for automated control evidence.
          </p>
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)} style={{ ...btnPrimary, padding: "7px 14px", fontSize: 13, cursor: "pointer", flexShrink: 0 }}>
            + Add Firewall
          </button>
        )}
      </div>

      {error && (
        <div style={{ ...card, marginBottom: 16, padding: "10px 14px", background: "var(--c-dangerBg)", border: "1px solid var(--c-danger)", color: "var(--c-dangerText)", fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Add form */}
      {showForm && (
        <div style={{ ...card, marginBottom: 24 }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600, color: theme.color.heading }}>Add Firewall</h3>

          {/* Vendor picker */}
          <div style={{ display: "flex", gap: 10, marginBottom: 16, marginTop: 12 }}>
            {(["fortinet", "palo_alto", "cisco"] as FirewallVendor[]).map((v) => {
              const active = form.vendor === v;
              const m = VENDOR_META[v];
              return (
                <button key={v} type="button" onClick={() => setField("vendor", v)} style={{
                  flex: 1, padding: "12px 10px", borderRadius: 3, fontSize: 13, fontWeight: active ? 700 : 400,
                  border: active ? `2px solid ${theme.color.accent}` : `1px solid ${theme.color.border}`,
                  background: active ? "var(--c-accentBg)" : "var(--c-panel)",
                  color: active ? "var(--c-accentText)" : theme.color.muted, cursor: "pointer",
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                }}>
                  <span style={{ fontSize: 20 }}>{m.icon}</span>
                  <span style={{ fontSize: 12 }}>{m.label}</span>
                </button>
              );
            })}
          </div>

          {/* Help text */}
          <div style={{ ...card, background: "var(--c-faint)", padding: "10px 14px", marginBottom: 16, fontSize: 12, color: theme.color.muted, lineHeight: 1.6 }}>
            {VENDOR_HELP[form.vendor]}
          </div>

          <form onSubmit={handleAdd} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
              <span style={{ fontWeight: 500, color: theme.color.text }}>Display Name</span>
              <input type="text" value={form.displayName} onChange={(e) => setField("displayName", e.target.value)}
                placeholder="Head Office Firewall" required style={{ ...inputStyle, fontSize: 13 }} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
              <span style={{ fontWeight: 500, color: theme.color.text }}>Host / IP Address</span>
              <input type="text" value={form.host} onChange={(e) => setField("host", e.target.value)}
                placeholder="192.168.1.1 or fw.example.com" required style={{ ...inputStyle, fontFamily: "monospace", fontSize: 13 }} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
              <span style={{ fontWeight: 500, color: theme.color.text }}>HTTPS Port <span style={{ fontWeight: 400, color: theme.color.muted }}>(optional, default 443)</span></span>
              <input type="number" value={form.port} onChange={(e) => setField("port", e.target.value)}
                placeholder="443" min={1} max={65535} style={{ ...inputStyle, fontFamily: "monospace", fontSize: 13 }} />
            </label>

            {meta.authFields === "apiKey" ? (
              <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
                <span style={{ fontWeight: 500, color: theme.color.text }}>API Key</span>
                <input type="password" value={form.apiKey} onChange={(e) => setField("apiKey", e.target.value)}
                  placeholder="API key from device" required style={{ ...inputStyle, fontSize: 13 }} />
              </label>
            ) : (
              <>
                <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
                  <span style={{ fontWeight: 500, color: theme.color.text }}>Username</span>
                  <input type="text" value={form.username} onChange={(e) => setField("username", e.target.value)}
                    placeholder="api_user" required style={{ ...inputStyle, fontSize: 13 }} />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
                  <span style={{ fontWeight: 500, color: theme.color.text }}>Password</span>
                  <input type="password" value={form.password} onChange={(e) => setField("password", e.target.value)}
                    placeholder="••••••••" required style={{ ...inputStyle, fontSize: 13 }} />
                </label>
              </>
            )}

            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10, marginTop: 4 }}>
              <button type="submit" disabled={saving}
                style={{ ...btnPrimary, padding: "9px 20px", fontSize: 13, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1 }}>
                {saving ? "Connecting…" : "Connect & Test"}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}
                style={{ padding: "9px 16px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: theme.color.muted, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* No connections */}
      {connections.length === 0 && !showForm && (
        <div style={{ ...emptyFrame, padding: "40px 28px" }}>
          <div style={{ marginBottom: 12, display: "flex", justifyContent: "center" }}><Plug size={36} color="var(--c-muted)" /></div>
          <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>
            No firewall connections yet
          </div>
          <p style={{ fontSize: 13, color: theme.color.muted, maxWidth: 480, margin: "0 auto", lineHeight: 1.6 }}>
            Connect Fortinet, Palo Alto, or Cisco firewalls to automatically pull policy snapshots as compliance evidence.
            The firewall's management interface must be reachable from this platform's servers.
          </p>
        </div>
      )}

      {/* Connection cards */}
      {connections.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {connections.map((fw) => {
            const si = fw.systemInfo;
            return (
              <div key={fw.id} style={{ ...card, padding: "18px 20px" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                      <span style={{ fontSize: 16 }}>{VENDOR_META[fw.vendor].icon}</span>
                      <span style={{ fontWeight: 700, fontSize: 15, color: theme.color.heading }}>{fw.displayName}</span>
                      <span style={{ background: "var(--c-successBg)", color: "var(--c-successText)", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 3 }}>
                        Connected
                      </span>
                    </div>
                    <VendorBadge vendor={fw.vendor} />
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => handleSync(fw.id)} disabled={syncing === fw.id}
                      style={{ ...btnPrimary, padding: "6px 13px", fontSize: 12, opacity: syncing === fw.id ? 0.6 : 1, cursor: syncing === fw.id ? "not-allowed" : "pointer" }}>
                      {syncing === fw.id ? "Syncing…" : "Sync"}
                    </button>
                    <button onClick={() => handleRemove(fw.id, fw.displayName)} disabled={removing === fw.id}
                      style={{ padding: "6px 13px", fontSize: 12, borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: "var(--c-dangerText)", cursor: "pointer", opacity: removing === fw.id ? 0.6 : 1 }}>
                      Remove
                    </button>
                  </div>
                </div>

                {/* Specs row */}
                <div style={{ display: "flex", gap: 20, marginTop: 14, flexWrap: "wrap", fontSize: 12, color: theme.color.muted }}>
                  <span>Host: <strong style={{ color: theme.color.text, fontFamily: "monospace" }}>{fw.host}{fw.port ? `:${fw.port}` : ""}</strong></span>
                  {si?.model    && <span>Model: <strong style={{ color: theme.color.text }}>{si.model}</strong></span>}
                  {si?.firmwareVersion && <span>Firmware: <strong style={{ color: theme.color.text, fontFamily: "monospace" }}>{si.firmwareVersion}</strong></span>}
                  {si?.serialNumber && <span>Serial: <strong style={{ color: theme.color.text, fontFamily: "monospace" }}>{si.serialNumber}</strong></span>}
                  {fw.policyCount != null && <span>Policies: <strong style={{ color: theme.color.text }}>{fw.policyCount}</strong></span>}
                  <span>Last sync: <strong style={{ color: theme.color.text }}>{fmtDate(fw.lastSync)}</strong></span>
                </div>

                <FirewallDetails key={fw.lastSync ?? "never"} id={fw.id} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
